import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import EngagementBar from '../../components/feature/EngagementBar';

export default function AlarmPackagesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [selectedService, setSelectedService] = useState('');
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);

  const serviceOptions = [
    'Home Basic Package',
    'Home Professional Package',
    'Home Premium Package',
    'Commercial Alarm System',
    'Not Sure — Need Advice',
  ];

  const packages = [
    {
      name: 'Home Basic',
      tier: 'STARTER',
      description: 'Essential protection for apartments & condos',
      color: 'bg-[#0A1628]',
      features: [
        'Wireless Control Panel',
        '2 Door/Window Sensors',
        '1 Motion Detector',
        'Back-up Battery',
        'Siren',
        'IP Communicator',
        'Mobile App Control',
        'Email & Push Alerts',
        '24/7 Professional Monitoring',
        'Professional Installation',
        '3-Year Equipment Parts & Labour Warranty'
      ],
      notIncluded: ['Video Verification', 'Smart Home Integration']
    },
    {
      name: 'Home Professional',
      tier: 'MOST POPULAR',
      description: 'Complete protection for homes & families',
      color: 'bg-[#D4A853]',
      featured: true,
      features: [
        'Touchscreen Control Panel',
        '3 Door/Window Sensors',
        '2 Motion Detectors',
        '1 Glass Break Sensor',
        'Back-up Battery',
        'Siren',
        'Mobile App Control',
        '24/7 Professional Monitoring',
        'Smart Home Integration',
        'Cellular Communication',
        'Professional Installation',
        '3-Year Equipment Parts & Labour Warranty'
      ],
      notIncluded: []
    },
    {
      name: 'Home Premium',
      tier: 'ULTIMATE',
      description: 'Maximum security for large properties',
      color: 'bg-[#0A1628]',
      features: [
        '7" HD Touchscreen Panel',
        '8 Door/Window Sensors',
        '2 Motion Detectors',
        '2 Glass Break Sensors',
        '1 Smoke Detector & 1 Carbon Monoxide Detector',
        'Water Leak Sensor',
        'Back-up Battery',
        'Siren',
        '24/7 Professional Monitoring',
        'Cellular Communication',
        'Full Smart Home Integration',
        'Professional Installation',
        '3-Year Equipment Parts & Labour Warranty'
      ],
      notIncluded: []
    }
  ];

  const faqs = [
    {
      question: 'What\'s included in professional installation?',
      answer: 'Professional installation includes a complete site assessment, strategic placement of all sensors and devices, system configuration, testing, and comprehensive training on how to use your new alarm system. Our certified technicians ensure everything works perfectly before they leave.'
    },
    {
      question: 'How does 24/7 monitoring work?',
      answer: 'When your alarm is triggered, our monitoring center is immediately notified. Our trained operators will attempt to contact you to verify the alarm. If we cannot reach you or you confirm an emergency, we dispatch local authorities to your location within seconds.'
    },
    {
      question: 'Can I control my alarm from my phone?',
      answer: 'Yes! All packages include mobile app control. You can arm/disarm your system, receive real-time alerts, view activity history, and manage user codes from anywhere using your smartphone or tablet.'
    },
    {
      question: 'What happens during a power outage?',
      answer: 'All our alarm systems include battery backup that keeps your system running for up to 24 hours during a power outage. Combined with cellular communication, your property remains fully protected even when the power is out.'
    }
  ];

  const alarmStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Alarm Packages', item: 'https://alliancesecuritysystems.com/alarm-packages' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://alliancesecuritysystems.com/alarm-packages#service',
      name: 'Alarm System Installation & Monitoring',
      description: 'Professional wireless alarm system installation with 24/7 monitoring, mobile app control, and 3-year warranty. Packages for homes, condos, and businesses across the GTA.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems',
        telephone: '+1-888-458-9181',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      areaServed: { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Alarm System Packages',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Home Basic Alarm Package',
              description: 'Essential alarm protection for apartments and condos. Includes wireless control panel, 2 door/window sensors, 1 motion detector, backup battery, siren, mobile app, and 24/7 professional monitoring.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Home Professional Alarm Package',
              description: 'Complete alarm protection for homes and families. Includes touchscreen panel, 3 door/window sensors, 2 motion detectors, glass break sensor, smart home integration, and cellular communication.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Home Premium Alarm Package',
              description: 'Maximum alarm security for large properties. Includes 7" HD touchscreen, 8 door/window sensors, 4 motion detectors, 2 glass break sensors, smoke & CO detectors, water leak sensor, and full smart home integration.',
            },
          },
        ],
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
    },
  ];

  const scrollToContact = () => {
    const el = document.getElementById('alarm-consultation-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('service', selectedService);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d6hp5a857726vudt81mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setSelectedService('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Smart Alarm Systems & Packages | Professional Installation | Alliance Security Systems"
        description="Choose from our Home Basic, Professional, or Premium alarm packages. All include 24/7 monitoring, mobile app control, professional installation & 3-year warranty. Protect your home with wireless alarm systems starting today. Free quote available."
        keywords="alarm systems, home alarm packages, wireless alarm system, 24/7 monitoring, smart home security, professional installation, alarm system Toronto, home security GTA"
        canonical="/alarm-packages"
        structuredData={alarmStructuredData}
        faqItems={faqs}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col leading-none">
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Home</Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/access-control" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Access Control</Link>
            <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Clients</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line"></i>1-888-458-9181
            </a>
            <button onClick={scrollToContact} className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">Get Free Quote</button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
            <Link to="/camera-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Camera Packages</Link>
            <Link to="/access-control" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Access Control</Link>
            <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"><i className="ri-phone-line"></i>1-888-458-9181</a>
            <button onClick={scrollToContact} className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Get Free Quote</button>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative py-12 sm:py-20 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#2A9D8F] cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>Smart Alarm Systems</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Packages</span>
            </nav>

            <div className="max-w-2xl">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A1628] mb-4 sm:mb-6 leading-tight">
                Choose Your Protection Level
              </h1>
              <p className="text-base sm:text-xl text-gray-600 leading-relaxed">
                Flexible alarm packages designed for every home and budget. All packages include professional installation and our industry-leading mobile app.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Cards Section */}
        <section className="py-10 sm:py-16 bg-white" aria-labelledby="packages-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 id="packages-heading" className="sr-only">Alarm System Packages</h2>
            {/* Package Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start">
              {packages.map((pkg, index) => (
                <div key={index} className={`relative bg-white rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${pkg.featured ? 'ring-2 ring-[#D4A853] md:-mt-4 md:mb-4' : ''}`}>
                  <div className={`h-2 ${pkg.color}`}></div>
                  {pkg.featured && (
                    <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-[#D4A853] text-white px-3 sm:px-4 py-1 rounded-full text-xs font-bold">{pkg.tier}</div>
                  )}
                  <div className="p-6 sm:p-10">
                    {!pkg.featured && <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{pkg.tier}</span>}
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mt-2 mb-2">{pkg.name}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">{pkg.description}</p>
                    <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${pkg.featured ? 'bg-[#D4A853]' : 'bg-emerald-500'}`}>
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                      {pkg.notIncluded.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 opacity-50">
                          <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-close-line text-white text-xs"></i>
                          </div>
                          <span className="text-gray-500 text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={scrollToContact} className={`w-full py-3.5 sm:py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${pkg.featured ? 'bg-[#D4A853] text-white hover:bg-[#B8923D] shadow-lg hover:shadow-xl' : 'border-2 border-[#0A1628] text-[#0A1628] hover:bg-[#0A1628] hover:text-white'}`}>Call for Quote</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-8 sm:py-12 bg-[#D4A853]/10" aria-label="Trust indicators">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {[
                { icon: 'ri-home-heart-line', stat: '30,000+', label: 'Homes Protected' },
                { icon: 'ri-time-line', stat: '24/7', label: 'Professional Monitoring' },
                { icon: 'ri-star-line', stat: '4.9★', label: 'Customer Rating' },
                { icon: 'ri-user-settings-line', stat: '100%', label: 'Licensed Technicians' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-md">
                    <i className={`${item.icon} text-xl sm:text-2xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-xl sm:text-3xl font-bold text-[#0A1628] mb-1">{item.stat}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-24 bg-[#F8F9FA]" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 id="faq-heading" className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Frequently Asked Questions</h2>
              <p className="text-sm sm:text-lg text-gray-600">Everything you need to know about our alarm packages</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-4 sm:p-6 text-left cursor-pointer">
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-[#D4A853] transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Optional Add-ons Section */}
        <section className="py-12 sm:py-24 bg-white" aria-labelledby="addons-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                ENHANCE YOUR SYSTEM
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Optional Add-ons
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Customize your alarm system with these additional devices for complete home protection and smart home integration
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-fire-line',
                  name: 'Smoke Detectors',
                  description: 'Early fire detection with instant alerts to your phone and monitoring center',
                  color: 'bg-red-500'
                },
                {
                  icon: 'ri-gas-station-line',
                  name: 'Carbon Monoxide Detectors',
                  description: 'Protect your family from deadly CO gas with 24/7 monitoring',
                  color: 'bg-orange-500'
                },
                {
                  icon: 'ri-drop-line',
                  name: 'Flood Detectors',
                  description: 'Water leak sensors for basements, bathrooms, and laundry rooms',
                  color: 'bg-blue-500'
                },
                {
                  icon: 'ri-door-lock-box-line',
                  name: 'Video Doorbell',
                  description: 'See and speak to visitors from anywhere with HD video and two-way audio',
                  color: 'bg-[#D4A853]'
                },
                {
                  icon: 'ri-camera-lens-line',
                  name: 'WiFi Cloud Cameras',
                  description: 'Indoor/outdoor cameras with cloud recording and mobile viewing',
                  color: 'bg-purple-500'
                },
                {
                  icon: 'ri-lock-2-line',
                  name: 'Smart Door Locks',
                  description: 'Keyless entry with remote lock/unlock and access codes',
                  color: 'bg-emerald-500'
                },
                {
                  icon: 'ri-lightbulb-line',
                  name: 'Smart Lighting',
                  description: 'Control lights remotely and create automated schedules',
                  color: 'bg-yellow-500'
                },
                {
                  icon: 'ri-home-gear-line',
                  name: 'Garage Door Integration',
                  description: 'Open, close, and monitor your garage door from your phone',
                  color: 'bg-gray-700'
                }
              ].map((addon, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 group"
                >
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 ${addon.color} rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform`}>
                    <i className={`${addon.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0A1628] mb-2">{addon.name}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-4">{addon.description}</p>
                  <div className="flex items-center gap-2 text-[#D4A853] text-xs sm:text-sm font-semibold">
                    <span>Additional cost</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 sm:mt-12 text-center">
              <p className="text-gray-600 text-sm sm:text-base mb-6">
                Want to add any of these devices to your system? Contact us for pricing and availability.
              </p>
              <button 
                onClick={scrollToContact}
                className="bg-[#D4A853] text-white px-8 py-3.5 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg inline-flex items-center gap-2"
              >
                Request Custom Quote
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Free Consultation Form Section */}
        <section id="alarm-consultation-form" className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                  FREE CONSULTATION
                </span>
                <h2 className="text-3xl sm:text-5xl font-bold text-[#0A1628] mb-6 leading-tight">
                  Get a Free Alarm System Quote
                </h2>
                <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                  Not sure which alarm package is right for you? Tell us about your property and our security experts will recommend the perfect solution — completely free, no obligation.
                </p>

                <div className="space-y-6 mb-10">
                  {[
                    { icon: 'ri-timer-line', title: 'Fast Response', desc: 'We\'ll get back to you within 2 business hours' },
                    { icon: 'ri-home-smile-line', title: 'Home Assessment', desc: 'Free walkthrough to identify your security needs' },
                    { icon: 'ri-price-tag-3-line', title: 'Transparent Pricing', desc: 'Detailed quote with no hidden fees or surprises' },
                    { icon: 'ri-shield-check-line', title: 'No Obligation', desc: 'Zero pressure — the consultation is completely free' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-[#0A1628] mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 p-5 bg-[#0A1628] rounded-2xl">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs mb-0.5">Prefer to talk now?</p>
                    <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-3xl p-8 sm:p-10 border border-gray-200">
                {formStatus === 'success' ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <i className="ri-check-line text-4xl text-green-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Request Received!</h3>
                    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                      Thank you for your interest. Our alarm specialists will contact you within 2 business hours with a personalized quote.
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-2">Get Your Free Quote</h3>
                    <p className="text-sm text-gray-500 mb-8">Fill out the form below and we'll reach out shortly.</p>

                    <form
                      id="alarm-consultation"
                      data-readdy-form
                      onSubmit={handleFormSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Full Name *</label>
                          <input
                            type="text"
                            name="fullName"
                            required
                            placeholder="John Smith"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            placeholder="(416) 555-0123"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="john@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Interested Package *</label>
                        <input type="hidden" name="service" value={selectedService} />
                        <button
                          type="button"
                          onClick={() => setServiceDropdownOpen(!serviceDropdownOpen)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all cursor-pointer"
                        >
                          <span className={selectedService ? 'text-[#0A1628]' : 'text-gray-400'}>
                            {selectedService || 'Select a package'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${serviceDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        {serviceDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto">
                            {serviceOptions.map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => {
                                  setSelectedService(option);
                                  setServiceDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#D4A853]/10 transition-colors cursor-pointer ${
                                  selectedService === option ? 'text-[#D4A853] font-semibold bg-[#D4A853]/5' : 'text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Property Type</label>
                          <input
                            type="text"
                            name="propertyType"
                            placeholder="e.g. Detached House, Condo"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Number of Entry Points</label>
                          <input
                            type="text"
                            name="entryPoints"
                            placeholder="e.g. 3 doors, 8 windows"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Additional Details</label>
                        <textarea
                          name="details"
                          rows={4}
                          maxLength={500}
                          placeholder="Tell us about your home, any existing security setup, and specific concerns..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting' || !selectedService}
                        className="w-full bg-[#D4A853] text-white py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Request Free Quote
                            <i className="ri-arrow-right-line text-lg"></i>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our <Link to="/privacy-policy" className="underline hover:text-[#D4A853] cursor-pointer">Privacy Policy</Link>. We'll never share your information.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 sm:py-20 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Still not sure which package is right for you?</h2>
            <p className="text-base sm:text-xl text-white/70 mb-6 sm:mb-8">Speak with a security expert who can assess your needs and recommend the perfect solution.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button onClick={scrollToContact} className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl inline-flex items-center justify-center gap-3">
                <i className="ri-calendar-line text-xl"></i>Schedule Free Consultation
              </button>
              <a href="tel:18884589181" className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-3">
                <i className="ri-phone-line text-xl"></i>1-888-458-9181
              </a>
            </div>
            <p className="text-white/50 text-xs sm:text-sm mt-4">No obligation • Response within 2 hours</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <Link to="/" className="flex items-center gap-3"><div className="flex flex-col leading-none"><span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span><span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span></div></Link>
            <p className="text-white/60 text-xs sm:text-sm">© 2024 Security Solutions. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>

      <EngagementBar />
    </div>
  );
}
